import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Card, Col, Row, App, Typography, Table } from 'antd';
import { LeftOutlined } from '@ant-design/icons';
import { getEvaluationById, evaluateVideo } from '../../services/evaluations';
import { resolveMedia } from '../../utils/media';
import GuiaPDF from '../../assets/Guía de Puntuación Videos.pdf';
import usePermissions from '../../hooks/usePermissions';

const { Title } = Typography;
const { useApp } = App;

const PreliminaryRateVideoPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { message } = useApp();
  const { can } = usePermissions();

  const [loading, setLoading] = useState(false);
  const [evaluation, setEvaluation] = useState(null);

  const columns = [
    {
      title: 'Criterio',
      render: (_, record) => record.criteria?.description,
    },
    {
      title: 'Puntuación máxima',
      dataIndex: 'score',
      align: 'center',
      render: (_, record) => {
        const isTotal = record.isTotal;
        return (
          <span
            style={{
              color: isTotal ? '#3F8600' : 'inherit',
              fontWeight: isTotal ? 600 : 400,
            }}
          >
            {_}
          </span>
        );
      },
    },
    {
      title: 'Puntuación IA',
      dataIndex: 'aiScore',
      align: 'center',
      render: (_, record) => Number((_ || 0).toFixed(2)) 
    },
  ];

  useEffect(() => {
    if (!id) return;

    setLoading(true);
    getEvaluationById(id)
      .then((evaluation) => {
        const criteriaWithAIScore = (evaluation?.competition?.criteria || []).map(c => {
          let aiScore = 0;
          switch (c.criteria?.name || c.criteria?.description) {
            case 'Dificultad/Técnica':
              aiScore = evaluation?.aiTechnicalScore;
              break;
            case 'Importancia/Contexto':
              aiScore = evaluation?.aiImportanceScore;
              break;
            case 'Creatividad/Originalidad':
              aiScore = evaluation?.aiCreativityScore;
              break;
            case 'Impacto visual/Presentación':
              aiScore = evaluation?.aiVisualScore;
              break;
            case 'Legitimidad/Conducta':
              aiScore = evaluation?.aiLegitimacyScore;
              break;
            default:
              aiScore = 0;
          }
          aiScore = (aiScore / 10) * c.score
          return {
            ...c,
            aiScore,
          };
        });

        const totalMax = criteriaWithAIScore.reduce(
          (acc, item) => acc + (item.score || 0),
          0
        );

        const totalAI = criteriaWithAIScore.reduce(
          (acc, item) => acc + (item.aiScore || 0),
          0
        );

        criteriaWithAIScore.push({
          id: 'total',
          criteria: { description: 'Total' },
          score: totalMax,
          aiScore: totalAI,
          isTotal: true,
        });
        
        const evaluationAI = {
          ...evaluation,
          competition: {
            ...(evaluation.competition || {}),
            criteria: criteriaWithAIScore,
          },
        };

        setEvaluation(evaluationAI);

      })
      .catch(() => message.error('No se pudo cargar la evaluación'))
      .finally(() => setLoading(false));
  }, [id, message]);

  const handleDecision = async (status) => {
    try {
      setLoading(true);
      await evaluateVideo(id, { status });
      message.success(
        status === 'ACCEPTED'
          ? 'Video aceptado correctamente'
          : 'Video rechazado correctamente'
      );
      navigate(`/evaluations/preliminary/${evaluation?.competition?.id}/evaluate`);
    } catch(error) {
      console.log(error);
      message.error(error?.response?.data?.error?.message || error?.response?.data?.message || 'No se pudo actualizar el estado del video');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Button
          type="text"
          icon={<LeftOutlined style={{ fontSize: 24 }} />}
          onClick={() => navigate(-1)}
        />
        <Title level={3} style={{ marginBottom: 0 }}>
          Evaluación preliminar video
        </Title>
      </div>

      <Card loading={loading}>
        <Title level={4}>
          Video a evaluar: {evaluation?.competition?.modality?.name || evaluation?.competition?.modality?.description}{' '}
            Región {evaluation?.competition?.regions?.length > 0
            ? evaluation.competition.regions
                .map(r => r.region?.description)
                .filter(Boolean)
                .join(', ')
            : evaluation?.region?.description || '-'}{' '}
            Competición {evaluation?.competition?.gender?.name || evaluation?.competition?.gender?.description}{' '}
        </Title>

        <Row gutter={24}>
          <Col xs={24} md={10}>
            <video
              src={resolveMedia(evaluation?.urlVideo)}
              controls
              style={{ width: '100%', borderRadius: 8 }}
            />

            <div style={{ marginTop: 16 }}>
              <strong>{evaluation?.folio || '—'}</strong>
              <br />
              Usuario: {evaluation?.playerName || '—'}
            </div>
            <Row style={{ marginTop: 32 }}>
              <Col xs={20}>
                <Table
                  rowKey="id"
                  dataSource={evaluation?.competition?.criteria || []}
                  columns={columns}
                  pagination={false}
                  scroll={{ x: 'max-content' }}
                />
              </Col>
            </Row>
          </Col>

          <Col xs={24} md={14}>
            <div style={{ borderBottom: '1px solid #E5E5E5', paddingBottom: 12, marginBottom: 12 }}>
              <strong>Análisis del video</strong>
              <div>{evaluation?.urlVideo?.split('/').pop() || '—'}</div>
            </div>

            <div style={{ borderBottom: '1px solid #E5E5E5', paddingBottom: 12, marginBottom: 12 }}>
              <strong>Tipo de jugada</strong>
              <div>[{evaluation?.aiCategory || '—'}]</div>
            </div>

            <div style={{ borderBottom: '1px solid #E5E5E5', paddingBottom: 12, marginBottom: 12 }}>
              <strong>Descripción</strong>
              <div style={{ textAlign: 'justify' }}>
                {evaluation?.aiContext || '—'}
              </div>
            </div>

            <Title
              level={5}
              style={{
                color: '#BA955E',
                marginTop: 24,
                marginBottom: 16,
                fontWeight: 600,
              }}
            >
              DICTAMEN DEL JURADO
            </Title>

            <div style={{ borderBottom: '1px solid #E5E5E5', paddingBottom: 12, marginBottom: 12 }}>
              <strong>Justificación técnica:</strong>
              <div style={{ textAlign: 'justify' }}>
                {evaluation?.aiJustification || '—'}
              </div>
            </div>

            <div style={{ borderBottom: '1px solid #E5E5E5', paddingBottom: 12, marginBottom: 12 }}>
              <strong>Análisis del contexto:</strong>
              <div style={{ textAlign: 'justify' }}>
                {evaluation?.aiContextAnalysis || '—'}
              </div>
            </div>

            <div>
              <strong>Conclusión:</strong>
              <div style={{ textAlign: 'justify' }}>
                {evaluation?.aiConclusion || '—'}
              </div>
            </div>
          </Col>
          <Col span={12}> <a href={GuiaPDF} target="_blank" rel="noopener noreferrer" style={{ color: '#BA955E' }} > Consultar criterios de evaluación (PDF) </a> </Col>
        </Row>

        {['EVALUATED'].includes(evaluation?.status) && can('preliminary.approve') &&
          (
            <Row justify="end" gutter={12} style={{ marginTop: 24 }}>
              <Col>
                <Button danger onClick={() => handleDecision('REJECTED')} loading={loading}>
                  Rechazar
                </Button>
              </Col>
              <Col>
                <Button
                  type="primary"
                  onClick={() => handleDecision('ACCEPTED')}
                  loading={loading}
                >
                  Aceptar
                </Button>
              </Col>
            </Row>
          )
        }
      </Card>

    </div>
  );
};

export default PreliminaryRateVideoPage;
